<script setup>
import { computed } from 'vue';
import { TabsRoot, TabsList, TabsTrigger, TabsIndicator } from 'reka-ui';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: wootConstants.ASSIGNEE_TYPE.ME,
  },
});

const emit = defineEmits(['chatTabChange']);

const activeTabIndex = computed(() => {
  return props.items.findIndex(item => item.key === props.activeTab);
});

const onTabChange = selectedTabIndex => {
  if (selectedTabIndex >= 0 && selectedTabIndex < props.items.length) {
    const selectedItem = props.items[selectedTabIndex];
    if (selectedItem.key !== props.activeTab) {
      emit('chatTabChange', selectedItem.key);
    }
  }
};

const onTabSelect = key => {
  onTabChange(props.items.findIndex(item => item.key === key));
};

const keyboardEvents = {
  'Alt+KeyN': {
    action: () => {
      if (props.activeTab === wootConstants.ASSIGNEE_TYPE.ALL) {
        onTabChange(0);
      } else {
        const nextIndex = (activeTabIndex.value + 1) % props.items.length;
        onTabChange(nextIndex);
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <TabsRoot
    :model-value="activeTab"
    class="w-full px-3 -mt-1"
    @update:model-value="onTabSelect"
  >
    <TabsList class="relative flex items-center gap-4 h-10">
      <TabsIndicator
        class="absolute bottom-0 start-0 h-[2px] w-[var(--reka-tabs-indicator-size)] translate-x-[var(--reka-tabs-indicator-position)] rounded-full bg-gradient-to-r from-n-lubb-cyan to-n-lubb-violet transition-[width,transform] duration-300 ease-out"
      />
      <TabsTrigger
        v-for="item in items"
        :key="item.key"
        :value="item.key"
        class="flex items-center h-full select-none text-sm font-medium text-n-slate-11 outline-none transition-colors hover:text-n-slate-12 data-[state=active]:text-n-brand focus-visible:text-n-slate-12"
      >
        {{ item.name }}
        <span
          v-if="item.count !== undefined"
          class="rounded-full h-5 flex items-center justify-center text-xs font-medium ms-1 px-1.5 py-0 min-w-[20px] transition-colors"
          :class="
            item.key === activeTab
              ? 'bg-n-brand/10 text-n-brand'
              : 'bg-n-alpha-1 text-n-slate-10'
          "
        >
          {{ item.count }}
        </span>
      </TabsTrigger>
    </TabsList>
  </TabsRoot>
</template>
